import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Calendar, MapPin, Ticket, ArrowUpRight, Plus } from "lucide-react";

const formatDate = (iso) => {
  const d = new Date(iso + "T00:00:00");
  return {
    day: d.toLocaleDateString("en-US", { day: "2-digit" }),
    month: d.toLocaleDateString("en-US", { month: "short" }).toUpperCase(),
    year: d.getFullYear(),
    weekday: d.toLocaleDateString("en-US", { weekday: "long" }).toUpperCase(),
  };
};

const buildICS = (gig) => {
  const start = new Date(`${gig.date}T20:00:00`);
  const end = new Date(start.getTime() + 3 * 60 * 60 * 1000);
  const fmt = (d) => d.toISOString().replace(/[-:]/g, "").split(".")[0] + "Z";
  const ics = [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "BEGIN:VEVENT",
    `UID:${gig.id}@retrofitmusic`,
    `DTSTAMP:${fmt(new Date())}`,
    `DTSTART:${fmt(start)}`,
    `DTEND:${fmt(end)}`,
    `SUMMARY:Retrofit @ ${gig.venue_name}`,
    `LOCATION:${gig.venue_address}`,
    `DESCRIPTION:${(gig.notes || "Retrofit live").replace(/\n/g, "\\n")}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\n");
  const blob = new Blob([ics], { type: "text/calendar" });
  return URL.createObjectURL(blob);
};

const Gigs = () => {
  const [gigs, setGigs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/gigs").then((r) => setGigs(r.data)).finally(() => setLoading(false));
  }, []);

  const upcoming = gigs.filter((g) => g.date >= new Date().toISOString().split("T")[0]);

  return (
    <div data-testid="gigs-page" className="min-h-screen">
      <section className="pt-20 pb-12 border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          <p className="eyebrow fade-up">Live Schedule</p>
          <h1 className="font-heading text-6xl md:text-8xl uppercase leading-[0.9] mt-5 fade-up-1" data-testid="gigs-title">
            Upcoming Gigs
          </h1>
          <p className="mt-6 max-w-2xl text-lg text-neutral-300 fade-up-2">
            Come find us. Drinks are cold, the dancefloor is loud, and the setlist is fire.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="max-w-[1400px] mx-auto px-5 md:px-10">
          {loading && <p className="font-mono text-neutral-400" data-testid="gigs-loading">Loading shows...</p>}
          {!loading && upcoming.length === 0 && (
            <div className="text-center py-20" data-testid="gigs-empty">
              <p className="font-heading text-3xl uppercase">No shows on the books right now.</p>
              <p className="mt-3 text-neutral-400">Check back soon, or book us for your event.</p>
            </div>
          )}

          <ul className="divide-y divide-white/10 border-t border-white/10">
            {upcoming.map((gig) => {
              const d = formatDate(gig.date);
              return (
                <li key={gig.id} className="py-8 md:py-10 grid md:grid-cols-12 gap-6 items-start hover:bg-white/[0.02] transition-colors px-2 md:px-4" data-testid={`gig-row-${gig.id}`}>
                  <div className="md:col-span-2">
                    <div className="font-mono text-xs text-neutral-400 tracking-[0.25em]">{d.weekday}</div>
                    <div className="font-heading text-5xl md:text-6xl leading-none mt-2">{d.day}</div>
                    <div className="font-mono text-sm mt-1">{d.month} {d.year}</div>
                    <div className="font-mono text-sm text-neutral-400 mt-1">{gig.time}</div>
                  </div>
                  <div className="md:col-span-6">
                    <h3 className="font-heading text-3xl md:text-4xl uppercase leading-tight" data-testid={`gig-venue-${gig.id}`}>{gig.venue_name}</h3>
                    {gig.venue_address && (
                      <div className="mt-3 flex items-start gap-2 text-neutral-300">
                        <MapPin size={16} className="mt-1 shrink-0" />
                        <span>{gig.venue_address}</span>
                      </div>
                    )}
                    {gig.notes && <p className="mt-3 text-sm text-neutral-400">{gig.notes}</p>}
                  </div>
                  <div className="md:col-span-4 flex flex-wrap gap-2 md:justify-end">
                    {gig.ticket_url && (
                      <a href={gig.ticket_url} target="_blank" rel="noreferrer" className="btn-retro !py-3 !px-4 !text-[10px]" data-testid={`gig-tickets-${gig.id}`}>
                        <Ticket size={14} className="mr-2" /> Tickets
                      </a>
                    )}
                    {gig.map_url && (
                      <a href={gig.map_url} target="_blank" rel="noreferrer" className="btn-retro !py-3 !px-4 !text-[10px]" data-testid={`gig-map-${gig.id}`}>
                        <MapPin size={14} className="mr-2" /> Directions <ArrowUpRight size={12} className="ml-1"/>
                      </a>
                    )}
                    {gig.time && gig.venue_address && (
                      <a href={buildICS(gig)} download={`retrofit-${gig.date}.ics`} className="btn-retro !py-3 !px-4 !text-[10px]" data-testid={`gig-calendar-${gig.id}`}>
                        <Plus size={14} className="mr-2" /> Calendar
                      </a>
                    )}
                  </div>
                </li>
              );
            })}
          </ul>
        </div>
      </section>
    </div>
  );
};

export default Gigs;
